import React, { useCallback, useState } from 'react';
import { useNavigate, useSearchParams as useUrlSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { ContentCopy as ContentCopyIcon, Search as SearchIcon } from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import * as locales from 'date-fns/locale';
import axios from 'axios';
import i18next, { getBrowserLanguage } from '../../lang';
import SortableTable, { SortableTableColumn } from '../../components/SortableTable';
import {rootStore} from "../../stores/RootStore";
import ConfirmDialog from "../../components/ConfirmDialog";
import MobileCardView, { MobileCardField } from '../../components/MobileCardView';
import { adminStyles } from '../../theme/adminStyles';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import InfiniteScrollFooter from '../../components/InfiniteScrollFooter';

export const getStatusText = (status: string) => {
  switch (status) {
    case 'CONFIRM':
      return i18next.t('order.order_confirmed');
    case 'SHIPPING_START':
      return i18next.t('order.shipping_started');
    case 'SHIPPING_COMPLETE':
      return i18next.t('order.shipping_completed');
    default:
      return '';
  }
};

export const getStatusColor = (status: string) => {
  switch (status) {
    case 'CONFIRM':
      return 'warning';
    case 'SHIPPING_START':
      return 'primary';
    case 'SHIPPING_COMPLETE':
      return 'success';
    default:
      return 'default';
  }
};

export const getStatusStyle = (status: string) => {
  switch (status) {
    case 'CONFIRM':
      return { bgcolor: '#d32f2f', color: '#fff' };
    case 'SHIPPING_START':
      return { bgcolor: '#fdd835', color: '#000' };
    case 'SHIPPING_COMPLETE':
      return { bgcolor: '#91CE15', color: '#fff' };
    default:
      return {};
  }
};

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  const date = new Date(dateStr.endsWith('Z') || dateStr.includes('+') ? dateStr : dateStr + 'Z');
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

interface InvoiceInfo {
  id: number;
  amount: number;
  dueDate: string;
  type: string;
  organizationName: string;
}

export interface OrderListItem {
  id: string;
  requestCompany: string;
  requesterId: string;
  totalAmount: number;
  status: string;
  mixed: boolean;
  createdAt: string;
  shippingCompletedDate: string | null;
  invoicedStatus: 'ALL' | 'PARTIAL' | 'NONE';
  invoices: InvoiceInfo[];
}

const OrderManagement: React.FC = () => {
  const { viewStatusStore, authStore } = rootStore;
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // URL クエリと同期 (戻るボタンで検索状態を復元するため)
  const [urlParams, setUrlParams] = useUrlSearchParams();
  const parseDate = (s: string | null) => s ? new Date(s) : null;
  const [searchParams, setSearchParams] = useState({
    status: urlParams.get('status') || '',
    startDate: parseDate(urlParams.get('startDate')),
    endDate: parseDate(urlParams.get('endDate')),
  });
  const [sortColumn, setSortColumn] = useState<string | null>(urlParams.get('sort') || null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    (urlParams.get('dir') === 'desc' ? 'desc' : 'asc') as 'asc' | 'desc'
  );

  const syncToUrl = useCallback((overrides?: {
    status?: string;
    startDate?: Date | null;
    endDate?: Date | null;
    sortColumn?: string | null;
    sortDirection?: 'asc' | 'desc';
  }) => {
    const next = new URLSearchParams();
    const status = overrides?.status !== undefined ? overrides.status : searchParams.status;
    const startDate = overrides?.startDate !== undefined ? overrides.startDate : searchParams.startDate;
    const endDate = overrides?.endDate !== undefined ? overrides.endDate : searchParams.endDate;
    const sCol = overrides?.sortColumn !== undefined ? overrides.sortColumn : sortColumn;
    const sDir = overrides?.sortDirection !== undefined ? overrides.sortDirection : sortDirection;

    if (status) next.set('status', status);
    if (startDate) next.set('startDate', startDate.toLocaleDateString('sv').split(' ')[0]);
    if (endDate) next.set('endDate', endDate.toLocaleDateString('sv').split(' ')[0]);
    if (sCol) next.set('sort', sCol);
    if (sCol && sDir !== 'asc') next.set('dir', sDir);

    setUrlParams(next, { replace: true });
  }, [searchParams.status, searchParams.startDate, searchParams.endDate, sortColumn, sortDirection, setUrlParams]);
  const [isInValidDateDialogOpen, setIsInValidDateDialogOpen] = useState(false);
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [invalidProductMessage, setInvalidProductMessage] = useState('');
  const [copyOrder, setCopyOrder] = useState('');

  // 청구서 다이얼로그
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [invoiceDialogData, setInvoiceDialogData] = useState<{ orderId: string; invoices: InvoiceInfo[] }>({ orderId: '', invoices: [] });

  const fetchOrders = useCallback(async (page: number, pageSize: number) => {
    const params = new URLSearchParams();
    if (searchParams.status) params.append('status', searchParams.status);
    if (searchParams.startDate) {
      const formattedStartDate = searchParams.startDate.toLocaleDateString('sv').split(' ')[0];
      params.append('startDate', formattedStartDate);
    }
    if (searchParams.endDate) {
      const formattedEndDate = searchParams.endDate.toLocaleDateString('sv').split(' ')[0];
      params.append('endDate', formattedEndDate);
    }
    params.append('page', page.toString());
    params.append('rowsPerPage', pageSize.toString());
    if (sortColumn) {
      params.append('sortColumn', sortColumn);
      params.append('sortDirection', sortDirection);
    }

    const response = await axios.get('/api/v1/orders', { params });
    return { items: response.data.orders, totalCount: response.data.totalCount };
  }, [searchParams.status, searchParams.startDate, searchParams.endDate, sortColumn, sortDirection]);

  const { items: orders, totalCount, isLoading, hasMore, sentinelRef, reload } = useInfiniteScroll<OrderListItem>({
    fetchFn: fetchOrders,
    pageSize: 20,
    resetDeps: [searchParams.status, sortColumn, sortDirection],
  });

  const handleSearch = () => {
    syncToUrl();
    reload();
  };

  const handleDetailClick = (orderId: number) => {
    navigate(`/orders/${orderId}`);
  };

  const handleCopyClick = async (orderId: number) => {
    try {
      viewStatusStore.setLoadingStatus({ open: true });

      const response = await axios.get(`/api/v1/orders/${orderId}/copy`);
      if (response.data.invalidProducts !== '') {
        setCopyOrder(JSON.parse(response.data.order));
        setInvalidProductMessage(response.data.invalidProducts);
        setIsCopyDialogOpen(true);
      } else {
        handleMoveOrder(JSON.parse(response.data.order));
      }
    } catch (error: any) {
      setInvalidProductMessage('');
      setCopyOrder('');
      console.error('Failed to copy order:', error);
    } finally {
      viewStatusStore.setLoadingStatus({ open: false });
    }
  };

  const handleMoveOrder = (copiedOrder: string) => {
    if (copiedOrder) {
      navigate('/purchase/order-input', { state: { tempOrder: copiedOrder } });
    } else {
      navigate('/purchase/order-input', { state: { tempOrder: copyOrder } })
    }
  }

  const handleSort = (columnKey: string) => {
    let newCol: string | null;
    let newDir: 'asc' | 'desc';
    if (sortColumn === columnKey) {
      if (sortDirection === 'asc') {
        newCol = columnKey;
        newDir = 'desc';
      } else {
        newCol = null;
        newDir = 'asc';
      }
    } else {
      newCol = columnKey;
      newDir = 'asc';
    }
    setSortColumn(newCol);
    setSortDirection(newDir);
    syncToUrl({ sortColumn: newCol, sortDirection: newDir });
  };

  const formatDateForTable = (dateString: string) => {
    const date = new Date(dateString + 'Z');
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const handleInvoiceClick = (e: React.MouseEvent, row: OrderListItem) => {
    e.stopPropagation();
    if (row.invoices && row.invoices.length > 0) {
      setInvoiceDialogData({ orderId: row.id, invoices: row.invoices });
      setInvoiceDialogOpen(true);
    }
  };

  const getInvoicedStatusLabel = (status: string) => {
    switch (status) {
      case 'ALL': return i18next.t('order.invoiced_all');
      case 'PARTIAL': return i18next.t('order.invoiced_partial');
      default: return i18next.t('order.invoiced_none');
    }
  };

  const getInvoicedStatusColor = (status: string) => {
    switch (status) {
      case 'ALL': return '#2e7d32';
      case 'PARTIAL': return '#ed6c02';
      default: return '#9e9e9e';
    }
  };

  const getInvoiceTypeLabel = (type: string) => {
    switch (type) {
      case 'TOTAL': return i18next.t('invoice.type_total');
      case 'BRAND': return i18next.t('invoice.type_brand');
      case 'CLASS': return i18next.t('invoice.type_class');
      default: return type;
    }
  };

  const columns: SortableTableColumn<OrderListItem>[] = [
    {
      key: 'id',
      label: i18next.t('order.order_number'),
      minWidth: 60,
      align: 'center',
    },
    {
      key: 'invoiced',
      label: i18next.t('order.invoiced'),
      minWidth: 70,
      align: 'center',
      sortable: false,
      renderCell: (row) => {
        const status = row.invoicedStatus || 'NONE';
        const hasInvoices = row.invoices && row.invoices.length > 0;
        return (
          <Typography
            component="span"
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: getInvoicedStatusColor(status),
              cursor: hasInvoices ? 'pointer' : 'default',
              textDecoration: hasInvoices ? 'underline' : 'none',
              '&:hover': hasInvoices ? { opacity: 0.7 } : {},
            }}
            onClick={(e) => handleInvoiceClick(e, row)}
          >
            {getInvoicedStatusLabel(status)}
          </Typography>
        );
      },
    },
    {
      key: 'requestCompany',
      label: i18next.t('order.orderer_name'),
      minWidth: 100,
      align: 'left',
    },
    {
      key: 'requestId',
      label: i18next.t('order.orderer_id'),
      minWidth: 60,
      align: 'center',
      renderCell: (row) => row.requesterId,
    },
    {
      key: 'totalAmount',
      label: i18next.t('invoice.total_amount'),
      minWidth: 100,
      align: 'center',
      renderCell: (row) => i18next.t('common.price_yen', { price: row.totalAmount.toLocaleString() }),
    },
    {
      key: 'status',
      label: i18next.t('common.status'),
      minWidth: 50,
      align: 'center',
      renderCell: (row) => (
        <Chip
          label={getStatusText(row.status) + (row.mixed ? ` (${i18next.t('order.mixed_status')})` : '')}
          sx={getStatusStyle(row.status)}
          size="small"
        />
      ),
    },
    {
      key: 'createdAt',
      label: i18next.t('order.order_date'),
      minWidth: 120,
      align: 'center',
      renderCell: (row) => formatDateForTable(row.createdAt),
    },
    {
      key: 'shippingCompletedDate',
      label: i18next.t('order.shipping_completed_date'),
      minWidth: 100,
      align: 'center',
      renderCell: (row) => {
        if (!row.shippingCompletedDate) return '';
        return row.shippingCompletedDate;
      },
    },
  ];

  const mobileCardFields: MobileCardField<OrderListItem>[] = [
    {
      key: 'requestCompany',
      label: i18next.t('order.requester'),
      primary: true,
    },
    {
      key: 'id',
      label: i18next.t('order.order_number'),
      getValue: (row) => row.id,
    },
    {
      key: 'invoiced',
      label: i18next.t('order.invoiced'),
      render: (row) => {
        const status = row.invoicedStatus || 'NONE';
        const hasInvoices = row.invoices && row.invoices.length > 0;
        return (
          <Typography
            component="span"
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: getInvoicedStatusColor(status),
              cursor: hasInvoices ? 'pointer' : 'default',
              textDecoration: hasInvoices ? 'underline' : 'none',
            }}
            onClick={(e) => handleInvoiceClick(e, row)}
          >
            {getInvoicedStatusLabel(status)}
          </Typography>
        );
      },
    },
    {
      key: 'status',
      label: i18next.t('common.status'),
      render: (row) => (
        <Chip
          label={getStatusText(row.status) + (row.mixed ? ` (${i18next.t('order.mixed_status')})` : '')}
          sx={getStatusStyle(row.status)}
          size="small"
        />
      ),
    },
    {
      key: 'totalAmount',
      label: i18next.t('order.total_amount'),
      getValue: (row) => row.totalAmount != null ? `¥${row.totalAmount.toLocaleString()}` : '',
    },
    {
      key: 'createdAt',
      label: i18next.t('order.order_date'),
      getValue: (row) => formatDate(row.createdAt),
    },
    {
      key: 'shippingCompletedDate',
      label: i18next.t('order.shipping_completed_date'),
      getValue: (row) => {
        if (!row.shippingCompletedDate) return '';
        if (row.shippingCompletedDate === 'MULTIPLE') return i18next.t('order.multiple_dates');
        return row.shippingCompletedDate;
      },
    },
    {
      key: 'actions',
      label: '',
      hideLabel: true,
      render: (row) => (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleDetailClick(Number(row.id));
            }}
            sx={{ fontSize: '0.75rem' }}
          >
            {i18next.t('common.details')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleCopyClick(Number(row.id));
            }}
            sx={{ fontSize: '0.75rem' }}
          >
            {i18next.t('order.copy_order')}
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Container maxWidth={false} sx={adminStyles.fullWidthPageContainer}>
      {/* 検索・フィルター */}
      <Paper elevation={isMobile ? 0 : undefined} sx={{ ...adminStyles.filterPaper as any, ...(isMobile && { p: 1.5, mb: 1.5, borderRadius: 0, boxShadow: 'none', border: 'none' }) }}>
        <Stack
          direction="row"
          spacing={isMobile ? 1 : 2}
          alignItems="center"
          flexWrap="nowrap"
        >
          <FormControl size="small" sx={{ minWidth: isMobile ? 0 : 100, flex: isMobile ? 1 : undefined, flexShrink: 0, '& .MuiInputBase-root': { height: 32, fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.8rem' } }}>
            <InputLabel>{i18next.t('common.status')}</InputLabel>
            <Select
              value={searchParams.status}
              label={i18next.t('common.status')}
              onChange={(e) => {
                const v = e.target.value;
                setSearchParams({ ...searchParams, status: v });
                syncToUrl({ status: v });
              }}
            >
              <MenuItem value="">{i18next.t('common.all')}</MenuItem>
              <MenuItem value="CONFIRM">{i18next.t('order.order_confirmed')}</MenuItem>
              <MenuItem value="SHIPPING_START">{i18next.t('order.shipping_started')}</MenuItem>
              <MenuItem value="SHIPPING_COMPLETE">{i18next.t('order.shipping_completed')}</MenuItem>
            </Select>
          </FormControl>

          <Stack direction="row" spacing={isMobile ? 1 : 2} sx={{ flex: isMobile ? 2 : undefined }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locales[getBrowserLanguage()] as any}>
              <DatePicker
                label={i18next.t('order.order_date_from')}
                value={searchParams.startDate}
                onChange={(newValue) => {
                  setSearchParams({ ...searchParams, startDate: newValue });
                  syncToUrl({ startDate: newValue });
                }}
                minDate={new Date(2024, 0, 1)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: isMobile,
                    inputProps: { readOnly: true },
                    sx: { '& .MuiInputBase-root': { height: 32, fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.8rem' } }
                  },
                  actionBar: { actions: ['clear'] }
                }}
              />
            </LocalizationProvider>

            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locales[getBrowserLanguage()] as any}>
              <DatePicker
                label={i18next.t('order.order_date_to')}
                value={searchParams.endDate}
                onChange={(newValue) => {
                  setSearchParams({ ...searchParams, endDate: newValue });
                  syncToUrl({ endDate: newValue });
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: isMobile,
                    inputProps: { readOnly: true },
                    sx: { '& .MuiInputBase-root': { height: 32, fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.8rem' } }
                  },
                  actionBar: { actions: ['clear'] }
                }}
              />
            </LocalizationProvider>
          </Stack>

          <Button
            variant="contained"
            size="small"
            onClick={handleSearch}
            sx={{ ...adminStyles.searchButton, flexShrink: 0, fontSize: '0.8rem', height: 32, ...(isMobile && { minWidth: 32, px: 0.5 }) }}
          >
            {isMobile ? <SearchIcon sx={{ fontSize: '1.1rem' }} /> : i18next.t('common.search')}
          </Button>
        </Stack>
      </Paper>

      {/* 주문 목록 */}
      <Paper sx={adminStyles.tablePaper}>
        {isMobile ? (
          <MobileCardView
            data={orders}
            fields={mobileCardFields}
            onRowClick={(row) => handleDetailClick(Number(row.id))}
            keyExtractor={(row) => row.id}
          />
        ) : (
          <SortableTable
            columns={columns}
            rows={orders}
            rowKey={(row) => row.id}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            onRowClick={(row) => handleDetailClick(Number(row.id))}
          />
        )}
        <InfiniteScrollFooter
          loadedCount={orders.length}
          totalCount={totalCount}
          isLoading={isLoading}
          hasMore={hasMore}
          sentinelRef={sentinelRef}
        />
      </Paper>

      {/* 청구서 정보 다이얼로그 */}
      <Dialog
        open={invoiceDialogOpen}
        onClose={() => setInvoiceDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {i18next.t('order.invoice_info')} (#{invoiceDialogData.orderId})
        </DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                    {i18next.t('invoice.serial_number')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                    {i18next.t('common.organization')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                    {i18next.t('invoice.type')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                    {i18next.t('invoice.total_amount')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                    {i18next.t('invoice.due_date')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoiceDialogData.invoices.map((inv) => (
                  <TableRow key={inv.id} hover>
                    <TableCell sx={{ fontSize: '0.8rem' }}>#{inv.id}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{inv.organizationName}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{getInvoiceTypeLabel(inv.type)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                      {i18next.t('common.price_yen', { price: inv.amount?.toLocaleString() })}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{inv.dueDate}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceDialogOpen(false)}>
            {i18next.t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
          open={isCopyDialogOpen}
          onClose={() => setIsCopyDialogOpen(false)}
          onConfirm={() => handleMoveOrder('')}
          title={i18next.t('order.copy_order')}
          message={`${i18next.t('order.price_changed_confirm')}\n\n${invalidProductMessage}`}
          button1Text={i18next.t('common.cancel')}
          button2Text={i18next.t('common.confirm')}
      />

      <ConfirmDialog
          open={isInValidDateDialogOpen}
          onClose={null}
          onConfirm={() => setIsInValidDateDialogOpen(false)}
          title={i18next.t('common.search_failed')}
          message={i18next.t('common.invalid_date')}
          button1Text={null}
          button2Text={i18next.t('common.confirm')}
      />
    </Container>
  );
};

export default OrderManagement;
